import React, { useContext, useState } from 'react';
import { Col, Descriptions, Dropdown, Row, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faPencil, faSortDown, faSortUp, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import Context from 'app/config/context';

type IChartNodeType = {
  items: any;
  idParent?: number;
  isSameOrganization?: boolean;
};

const colorsHeading = [
  '#4e542b',
  '#690029ed',
  '#265f2f',
  '#5f3518d9',
  '#19264ac7',
  '#4a5560',
  '#0f8d8f',
  '#DC143C',
  '#631460',
  '#530997',
  '#5F9EA0',
  '#D2691E',
  '#4b5598',
  '#20B2AA',
  '#FF7F50',
  '#008080',
  '#4B0082',
  '#BDB76B',
  '#2E8B57',
  '#8B0000',
  '#556B2F',
];

const colorsContent = [
  '#4e542b36',
  '#8000322b',
  '#dbedd4d9',
  '#9b743238',
  '#bcf7ffa8',
  '#dfe6ed',
  '#48f0f236',
  '#f1849a4d',
  '#ffddfe8a',
  '#e5c8ff82',
  '#85c3c51c',
  '#ffe0ca57',
  '#7a84c821',
  '#3afff40d',
  '#ffc4ae24',
  '#07ffff29',
  '#9200fc21',
  '#bdb76b1c',
  '#2e8b572e',
  '#ff171717',
  '#bee27f3b',
];

let colorIndex = 0;
const colorHeadingMap = new Map();
const colorContentMap = new Map();

const assignColorHeading = (id, currentId) => {
  if (colorHeadingMap.has(id)) {
    colorHeadingMap.set(currentId, colorHeadingMap.get(id));
    return colorHeadingMap.get(id);
  } else {
    if (colorIndex > colorsHeading.length) colorIndex = 0;
    const color = colorsHeading[colorIndex % colorsHeading.length];
    colorHeadingMap.set(id, color);
    return color;
  }
};

const assignColorContent = (id, currentId) => {
  if (colorContentMap.has(id)) {
    colorContentMap.set(currentId, colorContentMap.get(id));
    return colorContentMap.get(id);
  } else {
    const color = colorsContent[colorIndex % colorsContent.length];
    colorContentMap.set(id, color);
    colorIndex++;
    return color;
  }
};

const ChartNode = ({ items, idParent, isSameOrganization }: IChartNodeType) => {
  const [isChildrenCollapsed, setChildrenCollapsed] = useState(true);
  const { handleOpenModalAddItemOrg, handleOpenModalUpdateItemOrg, handleOpenModalConfirmDeleteItem } = useContext(Context);
  const toggleCollapse = () => {
    setChildrenCollapsed(!isChildrenCollapsed);
  };

  const sameOrg = isSameOrganization && idParent !== null ? idParent : items.id;
  const colorHeading = assignColorHeading(sameOrg, items.id);
  const colorContent = assignColorContent(sameOrg, items.id);

  const actions = [
    {
      key: 1,
      label: 'Cùng pháp nhân (Chi nhánh, Ban, Phòng, Bộ phận trực thuộc)',
    },
    {
      key: 2,
      label: 'Khác pháp nhân (Công ty thành viên)',
    },
  ];

  const onMenuClick = (e, item) => {
    const { key } = e;
    handleOpenModalAddItemOrg(item, key);
  };

  return (
    <li>
      <div id={items?.id} className={`oc-node`}>
        <div style={{ backgroundColor: colorHeading }} className={`oc-heading d-flex justify-content-between align-items-center gap-2`}>
          <Col md={17}>
            <Tooltip title={items?.orgName}>{items?.orgName}</Tooltip>
          </Col>
          <Col md={8}>
            <div className="d-flex gap-2">
              <Tooltip title="Thêm mới tổ chức cấp dưới">
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: actions,
                    onClick(e) {
                      onMenuClick(e, items);
                    },
                  }}
                  placement="bottomCenter"
                >
                  <FontAwesomeIcon icon={faUserPlus} className="cursor-pointer" />
                </Dropdown>
              </Tooltip>
              <Tooltip title="Sửa thông tin tổ chức">
                <FontAwesomeIcon
                  icon={faPencil}
                  className="cursor-pointer"
                  onClick={() => {
                    handleOpenModalUpdateItemOrg(items);
                  }}
                />
              </Tooltip>
              <Tooltip title="Xóa tổ chức">
                <FontAwesomeIcon
                  icon={faClose}
                  className="cursor-pointer"
                  onClick={() => {
                    handleOpenModalConfirmDeleteItem(items);
                  }}
                />
              </Tooltip>
            </div>
          </Col>
        </div>
        <div
          style={{ backgroundColor: colorContent, border: `1px solid ${colorContent}` }}
          className={`oc-content ${isChildrenCollapsed ? 'collapsed' : 'expanded'}`}
        >
          {items?.children && items.children.length > 0 && (
            <div onClick={toggleCollapse} className={`oc-button-arrow${isChildrenCollapsed ? '-down' : '-up'}`}>
              <FontAwesomeIcon icon={isChildrenCollapsed ? faSortDown : faSortUp} />
            </div>
          )}
          {items?.orgPosts?.map((item, i) => {
            return (
              <div key={`${item.postName}_${i}`} className="text-start ps-2">
                <Descriptions
                  className="text-nowrap"
                  items={[
                    {
                      contentStyle: { textOverflow: 'ellipsis', overflow: 'hidden', display: 'inline' },
                      label: (
                        <span className="fw-bold text-dark">
                          {item.postName}
                          {item.personPosts && item.personPosts.length > 1 && `(${item?.personPosts.length})`}
                        </span>
                      ),
                      children: <Tooltip title={item?.personPosts[0]}>{item?.personPosts[0]}</Tooltip>,
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>
      </div>
      {!isChildrenCollapsed && items.children && items.children.length > 0 && (
        <ul>
          {items.children.map(node => {
            return <ChartNode items={node} key={node.id} idParent={items.id} isSameOrganization={node.isSameOrganization} />;
          })}
        </ul>
      )}
    </li>
  );
};

export default ChartNode;
